// DepositRequiredOrdersPage.js
import React, { useState, useEffect } from "react";
import { useOrderChangesDialog } from "../../Components/orders/useOrderChangesDialog";
import { useQuery } from "@tanstack/react-query";
import { checkDuplicatePhoneCount, fetchDepositRequiredOrders, updateOrderStatus, updateFollowUpCheckpoint, deleteOrder, fetchDepositRequiredSummary, sendDepositInfoTemplate, sendCompleteOrderPromptTemplate } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle, teePlusPackedItemData } from "../../utils/orderLineDisplay";
import { copyOrderToTeePlus } from "../../utils/productionSource";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import OrderMobileCardList from "../../Components/orders/OrderMobileCardList";
import DepositFollowUpTimeline, { normalizeDepositFollowUpCheckpoint, DEPOSIT_FOLLOW_UP_VALUES } from "../../Components/orders/DepositFollowUpTimeline";
import StartConversationButton from "../../Components/orders/StartConversationButton";
import ViewConversationButton from "../../Components/orders/ViewConversationButton";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  FileText,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
  MessageSquare,
  Truck,
  AlertCircle,
  Layers,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Search,
  Loader,
  XCircle,
  RotateCcw,
  CreditCard,
  Percent,
  Wallet,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const calcDepositAmount = (total) => Math.ceil((Number(total || 0) * 0.25) / 5) * 5;

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const DepositRequiredOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const { changesDialog, openOrderChanges } = useOrderChangesDialog(async (savedOrderId) => {
    setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== savedOrderId));
    toast.success("تم تحديث الطلب إلى Changes بنجاح");
  });
  
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.4000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.4000");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  // expanded state: Set of orderNumbers that are currently expanded
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [sendingDepositTemplate1220, setSendingDepositTemplate1220] = useState(null);
  const [sendingCompleteOrderPrompt1525, setSendingCompleteOrderPrompt1525] = useState(null);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState(new Set());

  const BULK_SPAM_REASON = "مش عايز يدفع ديبوزت (أو مردش)";

  const toggleExpand = (orderNumber) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderNumber)) {
        next.delete(orderNumber);
      } else {
        next.add(orderNumber);
      }
      return next;
    });
  };

  const { data: depositRequiredSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["depositRequiredSummary"],
    queryFn: fetchDepositRequiredSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const isLoading = isLoadingSummary;
  const queryError = errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          status: order.status || "Deposit",
          paymentMethod: order.paymentMethod || "COD",
          paymentStatus: order.paymentStatus || "NotRequired",
          paymentTransactionId: order.paymentTransactionId || "",
          followUpCheckpoint: normalizeDepositFollowUpCheckpoint(order.followUpCheckpoint),
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchDepositRequiredOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setSelectedOrderNumbers(new Set());
    } catch (err) {
      console.error("Error loading initial deposit required orders:", err);
      setError("فشل في تحميل الطلبات التي تتطلب دفعة أولية. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchDepositRequiredOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more deposit required orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const incrementReference = () => {
    const match = currentReference.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[0], 10);
      const newNum = num + 1;
      const newRef = currentReference.replace(/\d+$/, newNum.toString());
      setCurrentReference(newRef);
      localStorage.setItem("orderReference", newRef);
    }
  };
  
  const handleUpdateStatus = async (orderId, newStatus, order) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");

      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      openOrderChanges(orderId);
      return;
    } else if (newStatus === "Spam") {
      reason = window.prompt("يرجى إدخال سبب :");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      setSelectedOrderNumbers(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      setSelectedOrderNumbers(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const toggleSelectOrder = (orderNumber) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const setSelectAllVisible = (checked, visibleOrders) => {
    setSelectedOrderNumbers((prev) => {
      const next = new Set(prev);
      if (checked) {
        visibleOrders.forEach((o) => next.add(o.orderNumber));
      } else {
        visibleOrders.forEach((o) => next.delete(o.orderNumber));
      }
      return next;
    });
  };

  const handleBulkSpamSelected = async () => {
    const ids = Array.from(selectedOrderNumbers);
    if (ids.length === 0) {
      toast.info("اختر أوردرات الأول");
      return;
    }

    const confirm = window.confirm(
      `هل أنت متأكد من تحويل ${ids.length} طلب(ات) إلى Spam؟\n\nالسبب: "${BULK_SPAM_REASON}"`
    );
    if (!confirm) return;

    toast.info("جاري تحويل المحدد إلى Spam... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "bulk-spam-deposit",
    });

    let successCount = 0;
    let failedCount = 0;

    for (const orderId of ids) {
      try {
        await updateOrderStatus(orderId, "Spam", BULK_SPAM_REASON);
        successCount++;
      } catch (err) {
        console.error(`Bulk Spam failed for order ${orderId}:`, err);
        failedCount++;
      }
    }

    setLoadedOrders((prev) => prev.filter((o) => !selectedOrderNumbers.has(o.orderNumber)));
    setSelectedOrderNumbers(new Set());

    toast.dismiss("bulk-spam-deposit");
    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى Spam بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleCopyMessage = (order) => {
    const itemsList = order.items.map(item => `النوع: ${item.type} - الكود: ${item.code}`).join('\n');

    const messages = [
      `أهلاً أ/ "${order.name}" 🌸
      مع حضرتك فاطمة من فريق جرابتكم ستور 💛
      بنراجع معاك تفاصيل الطلب قبل ما نبدأ التنفيذ، لأن كل تصميم بيتعمل مخصوص لك 🎨✨

      تفاصيل الطلب:
      ${itemsList}

      بمجرد تأكيدك هنبدأ فورًا في التجهيز والشحن ⚡🚚

      لتأكيد الطلب: رد بـ (نعم - تأكيد) ✅
      لو في تعديل أو إلغاء، قولنا قبل البدء 💬
      شكرًا لثقتك فينا 🤍🌷`,
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    copyToClipboard(randomMessage);
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  // دالة لتقريب الرقم لأقرب مضاعف 5
  const roundToNearest5 = (num) => {
    return Math.round(num / 5) * 5;
  };

  // دالة إرسال رسالة طلب الدفعة الأولية عبر الواتساب
  const handleSendDepositMessage = (order) => {
    const total = order.total || 0;
    const deposit = Math.round((total * 0.25) / 5) * 5; // تقريب 25% لأقرب مضاعف 5
    
    // جلب رقم الهاتف الأساسي للعميل
    let phoneNumber = order.phoneNumber || "";
    // تنظيف رقم الهاتف (إزالة أي أحرف غير أرقام)
    phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    // إضافة كود مصر إذا كان الرقم يبدأ بـ 0
    if (phoneNumber.startsWith('0')) {
      phoneNumber = '20' + phoneNumber.substring(1);
    }
    // إذا لم يبدأ بـ 20، أضف 20
    if (!phoneNumber.startsWith('20')) {
      phoneNumber = '20' + phoneNumber;
    }

const message = `مساء الخير يا فندم  
مع حضرتك مريم من جرابتكم ستور، حضرتك عامل أوردر من الويب سايت وكنت حابة أراجع مع حضرتك   

حابه أبلغ حضرتك إن سياسة الشركة بتنص إن أي أوردر 4 قطع أو أكتر بيحتاج دفع 25% من قيمته لتأكيد الطلب   

قيمة الدفعة: *${deposit} جنيه*  

مستنية تأكيد حضرتك `;

    // ترميز الرسالة للـ URL
    const encodedMessage = encodeURIComponent(message);
    // فتح رابط الواتساب
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
    
    toast.success(`جاري فتح واتساب للرقم ${order.phoneNumber}`);
  };

  const handleSendDepositInfoTemplate1220 = async (order) => {
    if (!order.phoneNumber || order.phoneNumber === "Unknown Phone") {
      toast.error("لا يوجد رقم موبايل صالح");
      return;
    }

    const deposit = calcDepositAmount(order.total);
    const confirmed = window.confirm(
      `إرسال قالب BeOn (83160) — معلومات العربون؟\n\n` +
      `الرقم: ${order.phoneNumber}\n` +
      `25% مقربة لأقرب 5: ${deposit} ج`
    );
    if (!confirmed) return;

    setSendingDepositTemplate1220(order.orderNumber);
    try {
      const data = await sendDepositInfoTemplate(order.orderNumber);
      if (data.success) {
        toast.success(
          data.message ||
            `تم إرسال قالب 83160 (عربون ${data.depositAmount ?? deposit} ج)`
        );
      } else {
        toast.warning(data.message || "فشل إرسال القالب");
      }
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingDepositTemplate1220(null);
    }
  };

  const syncFollowUpCheckpointState = (orderId, checkpoint) => {
    const normalized = normalizeDepositFollowUpCheckpoint(checkpoint);
    setLoadedOrders((prev) =>
      prev.map((order) =>
        order.orderNumber === orderId
          ? { ...order, followUpCheckpoint: normalized }
          : order
      )
    );
  };

  const handleFollowUpCheckpointUpdate = async (orderId, checkpoint) => {
    if (!canAct) return;
    try {
      await updateFollowUpCheckpoint(orderId, checkpoint);
      syncFollowUpCheckpointState(orderId, checkpoint);
      toast.success("تم تحديث مرحلة المتابعة");
    } catch (err) {
      toast.error(err.message || "فشل تحديث مرحلة المتابعة");
      throw err;
    }
  };

  const handleSendCompleteOrderPrompt1525 = async (order) => {
    if (!order.phoneNumber || order.phoneNumber === "Unknown Phone") {
      toast.error("لا يوجد رقم موبايل صالح");
      return;
    }

    const confirmed = window.confirm(
      `إرسال قالب BeOn (82959) — حابب تكمل الاوردر؟\n\nالرقم: ${order.phoneNumber}`
    );
    if (!confirmed) return;

    setSendingCompleteOrderPrompt1525(order.orderNumber);
    try {
      const data = await sendCompleteOrderPromptTemplate(order.orderNumber);
      if (data.success) {
        syncFollowUpCheckpointState(
          order.orderNumber,
          data.followUpCheckpoint || DEPOSIT_FOLLOW_UP_VALUES.WANT_TO_COMPLETE
        );
        toast.success(data.message || "تم إرسال قالب حابب تكمل الاوردر");
      } else {
        toast.warning(data.message || "فشل إرسال القالب");
      }
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingCompleteOrderPrompt1525(null);
    }
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      await copyOrderToTeePlus(finalText, order.orderNumber);

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const updateReferenceFromInput = () => {
    if (referenceInput.trim() && referenceInput.includes(".")) {
      setCurrentReference(referenceInput.trim());
      localStorage.setItem("orderReference", referenceInput.trim());
    }
  };

  // فلترة الطلبات حسب البحث
  const filteredOrders = loadedOrders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });
  const handleCopyProductsTypeCode = (order) => {
    const productsList = order.items.map((item, index) => {
      return `${index + 1}. ${item.type} - الكود: ${item.code}`;
    }).join('\n');
    
    const message = `المنتجات المطلوبة:\n${productsList}`;
    copyToClipboard(message);
  };

  if (queryError) {
    return <div>Error: {queryError.message}</div>;
  }









  // NEW FUNCTION FOR THE "دفع كام؟" BUTTON - Enhanced with number validation
  const handlePartialPayment = async (orderId, orderTotal) => {
    // Ask for the paid amount with a better message
    let paidAmount = window.prompt("كم دفع العميل؟ (أدخل أرقام فقط)\nمثال: 200");
    
    // Check if user cancelled
    if (paidAmount === null) return;
    
    // Clean and validate the input - remove any non-numeric characters except decimal point
    paidAmount = paidAmount.trim();
    if (!paidAmount) {
      toast.error("يجب إدخال المبلغ المدفوع!");
      return;
    }
    
    // Remove any non-numeric characters except digits and decimal point
    const numericOnly = paidAmount.replace(/[^0-9.]/g, '');
    if (!numericOnly) {
      toast.error("يرجى إدخال أرقام فقط!");
      return;
    }
    
    const paidNumber = parseFloat(numericOnly);
    if (isNaN(paidNumber) || paidNumber <= 0) {
      toast.error("يرجى إدخال رقم صحيح للمبلغ المدفوع!");
      return;
    }
    
    // Round to nearest 5 if you want (optional)
    // const paidNumberRounded = roundToNearest5(paidNumber);
    
    // Calculate the remaining amount
    const totalAmount = parseFloat(orderTotal);
    if (paidNumber > totalAmount) {
      toast.error("المبلغ المدفوع أكبر من تكلفة الأوردر!");
      return;
    }
    const remainingAmount = totalAmount - paidNumber;
    
    // Construct the special note
    const paymentNote = `دفع ${paidNumber} و الحساب ${remainingAmount}`;
    
    // Confirm the action
    const confirmMessage =
      `تأكيد:\n` +
      `العميل دفع: ${paidNumber} ج\n` +
      `تكلفة الأوردر الجديدة: ${remainingAmount} ج\n` +
      `الحالة: Changes\n` +
      `الملاحظة: "${paymentNote}"\n\nهل أنت متأكد؟`;
    const confirm = window.confirm(confirmMessage);
    if (!confirm) return;
    
    try {
      await updateOrderStatus(orderId, "Changes", paymentNote, null, { paidAmount: paidNumber });
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى "تغييرات" وتعديل التكلفة إلى ${remainingAmount} ج (بعد خصم ${paidNumber} ج)!`);
    } catch (err) {
      console.error("Error in handlePartialPayment:", err);
      toast.error("فشل التحديث: " + (err.message || "خطأ غير معروف"));
    }
  };

  // --- NEW FUNCTION FOR THE NORMAL "تغييرات" BUTTON ---
  const handleNormalChanges = async (orderId) => {
    if (!canAct) return;
    openOrderChanges(orderId);
  };









  

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      {changesDialog}
      <ToastContainer position="top-center" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col-2 sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <CreditCard className="text-orange-600" size={32} />
            <h1 className="text-xl md:text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              طلبات تتطلب دفعة أولية
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-2 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 text-sm md:text-base"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Stats Cards */}
        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <Package className="text-orange-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص طلبات الدفعة الأولية</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-orange-600">{depositRequiredSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{depositRequiredSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}



        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
        </div>

        {/* Bulk Select + Spam */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-gray-700 font-medium">
              المحدد: <span className="font-bold text-red-600">{selectedOrderNumbers.size}</span>
            </div>
            <button
              onClick={handleBulkSpamSelected}
              disabled={selectedOrderNumbers.size === 0}
              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <AlertTriangle size={18} />
              Spam المحدد
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-right">
            السبب ثابت: "{BULK_SPAM_REASON}"
          </p>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <OrderMobileCardList
                    orders={filteredOrders}
                    expandedOrders={expandedOrders}
                    toggleExpand={toggleExpand}
                    borderClass="border-orange-100"
                    dateClass="text-orange-600"
                    onDownload={handleDownload}
                    conversationPreset="deposit"
                    canSendConversationTemplates={canAct}
                    renderActions={(order) => (
                      <>
                        <label className="flex items-center gap-2 mb-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedOrderNumbers.has(order.orderNumber)}
                            onChange={() => toggleSelectOrder(order.orderNumber)}
                            className="w-5 h-5 accent-red-600"
                          />
                          <span className="text-sm font-medium text-red-700">
                            تحديد الأوردر #{order.orderNumber}
                          </span>
                        </label>
                        <DepositFollowUpTimeline
                          orderId={order.orderNumber}
                          checkpoint={order.followUpCheckpoint}
                          onUpdate={handleFollowUpCheckpointUpdate}
                          compact
                          className="mb-3"
                        />
                      {canAct ? (
                      <div className="grid grid-cols-3 gap-2">
                        {/* <button
                          type="button"
                          onClick={() => handleSendDepositMessage(order)}
                          className="col-span-3 bg-amber-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-1 active:scale-95 transition text-lg"
                        >
                          <Wallet size={13} />
                          واتساب دفعة 25%
                        </button> */}
                        <button
                          type="button"
                          onClick={() => handleSendCompleteOrderPrompt1525(order)}
                          disabled={sendingCompleteOrderPrompt1525 === order.orderNumber}
                          className="col-span-3 bg-indigo-600 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-50"
                        >
                          <MessageSquare size={13} />
                          {sendingCompleteOrderPrompt1525 === order.orderNumber ? "جاري الإرسال..." : "حابب تكمل الاوردر"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePartialPayment(order.orderNumber, order.total)}
                          className="col-span-3 bg-red-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-1 active:scale-95 transition text-lg"
                        >
                          <CreditCard size={18} />
                          دفع كام؟
                        </button>
                        <StartConversationButton
                          order={order}
                          className="col-span-3 py-3 rounded-xl"
                          hidden={!canAct}
                        />
                        <button
                          type="button"
                          onClick={() => handleSendDepositInfoTemplate1220(order)}
                          disabled={sendingDepositTemplate1220 === order.orderNumber}
                          className="col-span-3 bg-amber-500 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-1 active:scale-95 transition text-lg"
                        >
                          <Wallet size={13} />
                          {sendingDepositTemplate1220 === order.orderNumber ? "جاري الإرسال..." : "واتساب دفعة 25%"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleNormalChanges(order.orderNumber)}
                          className="col-span-2 bg-purple-500 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <RotateCcw size={14} />
                          تغييرات
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                          className="col-span-1 bg-gray-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <Archive size={13} />
                          قديم
                        </button>
                      </div>
                      ) : (
                        <p className="text-center text-sm text-slate-500 bg-slate-50 rounded-xl py-2 border border-slate-200">عرض فقط</p>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCopyProductsTypeCode(order)}
                        className="w-full bg-teal-500 text-white py-3 rounded-xl text-lg font-medium flex items-center justify-center gap-1 active:scale-95 transition mt-2"
                      >
                        <Layers size={13} />
                        نسخ المنتجات (النوع - الكود)
                      </button>
                      </>
                    )}
                    renderExpanded={(order) => (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          {canAct && (
                          <button
                            type="button"
                            onClick={() => handleSendDepositInfoTemplate1220(order)}
                            disabled={sendingDepositTemplate1220 === order.orderNumber}
                            className="bg-amber-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-50"
                          >
                            <Wallet size={13} />
                            {sendingDepositTemplate1220 === order.orderNumber ? "..." : " واتساب دفعة 25%"}
                          </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCopyAllExcelData(order)}
                            className="bg-green-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <Copy size={13} />
                            نسخ (TeePlus)
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => handleCopyProductsTypeCode(order)}
                            className="w-full bg-teal-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <Layers size={13} />
                            نسخ المنتجات
                          </button>
                          {canAct && (
                          <>
                          <button
                            type="button"
                            onClick={() => handleDeleteOrder(order.orderNumber)}
                            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <Trash2 size={13} />
                            حذف
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                          >
                            <AlertTriangle size={13} />
                            Spam
                          </button>
                          </>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCopyMessage(order)}
                          className="w-full bg-gray-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                        >
                          <MessageSquare size={13} />
                          رسالة للعميل
                        </button>
                      </>
                    )}
                  />

                  {/* Desktop Table */}
                  <table className="w-full min-w-[1200px] table-auto hidden md:table">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                        <th className="p-4 text-center font-bold text-purple-800">
                          <div className="flex items-center justify-center gap-2">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-purple-600"
                              checked={
                                filteredOrders.length > 0 &&
                                filteredOrders.every((o) => selectedOrderNumbers.has(o.orderNumber))
                              }
                              onChange={(e) => setSelectAllVisible(e.target.checked, filteredOrders)}
                            />
                            تحديد الكل
                          </div>
                        </th>
                        <th className="p-4 text-right font-bold text-purple-800">رقم الطلب</th>
                        <th className="p-4 text-right font-bold text-purple-800">العميل</th>
                        <th className="p-4 text-right font-bold text-purple-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-purple-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-purple-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-purple-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-purple-800 min-w-[160px]">متابعة</th>
                        <th className="p-4 text-center font-bold text-purple-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-purple-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-purple-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          <td className="p-4 text-center">
                            <input
                              type="checkbox"
                              className="w-5 h-5 accent-purple-600"
                              checked={selectedOrderNumbers.has(order.orderNumber)}
                              onChange={() => toggleSelectOrder(order.orderNumber)}
                            />
                          </td>
                          {/* Order Number */}
                          <td className="p-4">
                            <span className="font-mono font-bold text-purple-600">#{order.orderNumber}</span>
                           </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>
                            <br />
                            <div className="space-y-1">
                              <ViewConversationButton
                                order={order}
                                phone={order.phoneNumber}
                                templatePreset="deposit"
                                canSend={canAct}
                                compact
                                className="w-full"
                              />
                              <button
                                type="button"
                                onClick={() => copyToClipboard(order.phoneNumber)}
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 transition cursor-pointer"
                                title="نسخ الرقم"
                              >
                                <Phone size={14} />
                                {order.phoneNumber}
                              </button>
                              {order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" && order.phoneNumber2 !== "غير معروف" && (
                                <>
                                  <ViewConversationButton
                                    order={order}
                                    phone={order.phoneNumber2}
                                    templatePreset="deposit"
                                    canSend={canAct}
                                    compact
                                    className="w-full"
                                    label="عرض محادثة الرقم التاني"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(order.phoneNumber2)}
                                    className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm transition cursor-pointer"
                                    title="نسخ الرقم"
                                  >
                                    <Phone size={12} />
                                    {order.phoneNumber2}
                                  </button>
                                </>
                              )}
                            </div>
                           </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                              <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                           </td>

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                           </td>

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-teal-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                        {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                      
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-teal-600 hover:text-teal-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {item.code.startsWith("CUSTOM") && (
                                          <button
                                            onClick={() =>
                                              handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                            }
                                            className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                            title="تحميل الصورة"
                                            style={{
                                              backgroundColor: "#15d225",
                                              borderRadius: "4px",
                                            }}
                                          >
                                            <Download size={16} />
                                            <span className="text-sm">Download</span>
                                          </button>
                                        )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                           </td>

                          {/* Notes */}
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[15px]">{order.note || "—"}</p>
                           </td>

                          {/* Follow-up */}
                          <td className="p-2 align-top">
                            <DepositFollowUpTimeline
                              orderId={order.orderNumber}
                              checkpoint={order.followUpCheckpoint}
                              onUpdate={handleFollowUpCheckpointUpdate}
                              compact
                            />
                          </td>

                          {/* Copy Excel Button */}
                          <td className="p-4">
                            {canAct && (
                            <>
                            <button
                              onClick={() => handleSendCompleteOrderPrompt1525(order)}
                              disabled={sendingCompleteOrderPrompt1525 === order.orderNumber}
                              className="w-full text-xl bg-indigo-600 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3 disabled:opacity-50"
                            >
                              <MessageSquare size={16} />
                              {sendingCompleteOrderPrompt1525 === order.orderNumber ? "جاري الإرسال..." : "حابب تكمل الاوردر"}
                            </button>
                            <StartConversationButton
                              order={order}
                              className="w-full text-xl px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 font-bold mb-3"
                          hidden={!canAct}
                        />
                            <button
                              onClick={() => handleSendDepositInfoTemplate1220(order)}
                              disabled={sendingDepositTemplate1220 === order.orderNumber}
                              className="w-full text-xl bg-amber-700 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3 disabled:opacity-50"
                            >
                              <Wallet size={16} />
                              {sendingDepositTemplate1220 === order.orderNumber ? "جاري الإرسال..." : "واتساب دفعة 25%"}
                            </button>
                            </>
                            )}
                            <button
                              onClick={() => handleCopyAllExcelData(order)}
                              className="w-full text-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3"
                            >
                              <Copy size={16} />
                              نسخ (TeePlus)
                            </button>
                            {canAct && (
                            <>
                            <button
                              onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                              className="w-full text-xl bg-purple-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3"
                            >
                              <RotateCcw size={16} />
                               تغييرات
                              </button>
                              <button
                            type="button"
                            onClick={() => handlePartialPayment(order.orderNumber, order.total)}
                            className="w-full text-xl bg-red-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3"
                            >
                            <CreditCard size={18} />
                            دفع كام؟
                          </button>
                          </>
                          )}
                           </td>

                          {/* Actions */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleSendCompleteOrderPrompt1525(order)}
                                disabled={sendingCompleteOrderPrompt1525 === order.orderNumber}
                                className="bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <MessageSquare size={14} />
                                  {sendingCompleteOrderPrompt1525 === order.orderNumber ? "..." : "حابب تكمل الاوردر"}
                                </div>
                              </button>
                              <StartConversationButton order={order} className="w-full"
                          hidden={!canAct}
                        />
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                             
                              
                              {/* <button
                                onClick={() => handleCopyMessage(order)}
                                className="bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <MessageSquare size={14} />
                                  رسالة
                                </div>
                              </button> */}
                              {/* <button
                                onClick={() => handleSendDepositInfoTemplate1220(order)}
                                disabled={sendingDepositTemplate1220 === order.orderNumber}
                                className="bg-amber-700 text-white px-3 py-2 rounded-lg hover:bg-amber-800 disabled:opacity-50 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Wallet size={14} />
                                  {sendingDepositTemplate1220 === order.orderNumber ? "..." : "83160 عربون"}
                                </div>
                              </button> */}
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                           </td>

                          {/* Delete */}
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                           </td>
                         </tr>
                      ))}
                    </tbody>
                   </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <CreditCard className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تتطلب دفعة أولية</p>
                  <p className="text-lg text-gray-500">سيتم عرض الطلبات التي تحتاج دفعة أولية هنا</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Floating Action Button for Quick Reference Update */}
        <div className="fixed bottom-6 left-6">
          <button
            onClick={incrementReference}
            className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="زيادة الرقم المرجعي"
          >
            <Plus size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepositRequiredOrdersPage;